import type { Editor } from '@tiptap/core';
import type { ChangeEvent, MutableRefObject, ReactNode } from 'react';
import type { WYSIWYGProps, WYSIWYGHandle } from 'components/WYSIWYG/types';

import { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { layers } from '__app/spacing';
import { useLength } from '__hooks/useLength';
import { BasicRichTextEditor } from '__components/WYSIWYG';
import { getMergeFieldSuggestionOptions } from 'components/WYSIWYG/suggestions/merge-field';
import Validation, { useValidation } from 'components/Inputs/Validation';
import {
  StyledLabelWrapper,
  StyledLabel,
  StyledLabelRowWrapper,
  LimitLabel,
} from './styles';

export type TextTemplaterProps = {
  appendMergeFieldExtraSpace?: boolean;
  customize?: {
    autoSuggestions?: boolean;
    disableFocus?: boolean;
    mergeFields?: WYSIWYGProps['mergeFields'];
    showCountComponent?: boolean;
    showSelectComponent?: boolean;
    showLabel?: boolean;
    wrapperClassName?: string;
  };
  dataQA?: string;
  defaultValue?: string;
  disabled?: boolean;
  editorRef?: MutableRefObject<WYSIWYGHandle | null>;
  enableDisallowWhitespace?: boolean;
  encodeMergeFieldsInBody?: boolean;
  error?: boolean;
  inModal?: boolean;
  oneLineLayout?: boolean;
  oneLineHeader?: boolean;
  textareaOptions?: {
    placeholder?: string;
    enableResize?: boolean;
    enableToolbar?: boolean;
    initialHeight?: number;
    borderHoverColor?: string;
  };
  title?: string | ReactNode;
  validate?: { message: string | string[] | null; showMessage: boolean };
  wideMergePicker?: boolean;
  toggleDisabled?(): void;
  onBlur?(): void;
  onChange?(data: { editor: Editor }): void;
  preserveWhitespace?: WYSIWYGProps['preserveWhitespace'];
  onSave?(data: {
    content: string;
    htmlContent: string;
    textContent: string;
  }): void;
  onToggleEncoding?(event: ChangeEvent<HTMLInputElement>): void;
};

const customizeDefaultOptions = {
  showSelectComponent: true,
  showCountComponent: true,
  showLabel: true,
  disableFocus: false,
  wrapperClassName: '',
  mergeFields: null,
};

const getTextFromContent = (editor: Editor) => {
  return editor
    .getJSON()
    .content.map(({ content }) => {
      if (!content) {
        return '';
      }

      return content.reduce((acc, node) => {
        return 'text' in node ? acc + node.text : acc;
      }, '');
    })
    .join('\n');
};

const TextTemplater = (props: TextTemplaterProps) => {
  const {
    title: titleProp = null,
    textareaOptions = {},
    onSave,
    defaultValue = '',
    customize = {},
    toggleDisabled,
    disabled = false,
    appendMergeFieldExtraSpace = true,
    oneLineLayout = false,
    oneLineHeader = false,
    onChange,
    onToggleEncoding,
    encodeMergeFieldsInBody,
    enableDisallowWhitespace = false,
    dataQA,
    wideMergePicker,
    inModal = false,
    onBlur,
    error = false,
    validate,
    editorRef,
    preserveWhitespace,
  } = props;

  const { t } = useTranslation();
  const title = titleProp || t('Text Message Body');
  const [textareaValue, setTextareaValue] = useState(defaultValue || '');
  const saveFn = useRef(onSave);
  saveFn.current = onSave;

  const customizeOptions = { ...customizeDefaultOptions, ...customize };

  const [
    {
      maxLength,
      singleMessageCount,
      maxLengthForSingleMessage,
      countOfMessages,
      maxCountOfMessages,
      totalCount,
    },
    updateEvent,
  ] = useLength(textareaValue.length);

  useEffect(() => {
    // we need to update characters counter
    if (toggleDisabled && totalCount > maxLength !== disabled) toggleDisabled();
  }, [disabled, toggleDisabled, maxLength, totalCount]);

  const textareaChangeEvent = useCallback(
    (editor: Editor) => {
      const text = getTextFromContent(editor);
      const html = editor.getHTML();
      saveFn.current &&
        saveFn.current({
          content: editor.getText(), // default block separator is \n\n,
          textContent: editor.getText({ blockSeparator: '\n' }),
          htmlContent: html,
        });
      setTextareaValue(text);
      updateEvent(text);
    },
    [updateEvent]
  );

  const validationRef = useRef<HTMLDivElement>(null);
  const overflow = totalCount - maxLength;
  const [validation, validationProps] = useValidation(validationRef, {
    validate,
    value: null,
  });

  return (
    <StyledLabelWrapper
      className={customizeOptions.wrapperClassName}
      oneLineLayout={oneLineLayout}
      oneLineHeader={oneLineHeader}
      data-qa={dataQA}
      ref={validationRef}
    >
      {title && customizeOptions.showLabel && (
        <StyledLabel>{title}</StyledLabel>
      )}
      <BasicRichTextEditor
        ref={editorRef}
        autofocus={!customizeOptions.disableFocus}
        initialValue={textareaValue}
        onChange={({ editor }: { editor: Editor }) => {
          if (onChange) {
            onChange({ editor });
          }

          textareaChangeEvent(editor);
        }}
        onInit={({ editor }: { editor: Editor }) => {
          if (defaultValue) {
            updateEvent(getTextFromContent(editor));
          }
        }}
        appendMergeFieldExtraSpace={appendMergeFieldExtraSpace}
        enableMergeFields={customizeOptions.showSelectComponent}
        mergeFields={customizeOptions.mergeFields}
        enableDisallowWhitespace={enableDisallowWhitespace}
        enableLists={false}
        enableTextStyles={false}
        {...(oneLineLayout || oneLineHeader
          ? { ...textareaOptions, initialHeight: oneLineHeader ? 60 : 36 }
          : textareaOptions)}
        onToggleEncoding={onToggleEncoding}
        encodeMergeFieldsInBody={encodeMergeFieldsInBody}
        disabled={disabled}
        wideMergePicker={wideMergePicker}
        mergeFieldSuggestion={getMergeFieldSuggestionOptions({
          zIndex: inModal ? layers.modals(1) : undefined,
          props: { mergeFields: customizeOptions.mergeFields },
        })}
        onBlur={onBlur}
        error={error || validation.error}
        preserveWhitespace={preserveWhitespace}
      />

      {customizeOptions.showCountComponent && (
        <StyledLabelRowWrapper>
          <LimitLabel error={overflow > 0}>
            {overflow <= 0
              ? `${singleMessageCount}/${maxLengthForSingleMessage} ${t(
                  'characters left in message'
                )} ${countOfMessages} ${t('of')} ${maxCountOfMessages}`
              : `${t('You are')} ${overflow} ${
                  overflow === 1 ? t('character') : t('characters')
                } ${t('over the message limit')}`}
          </LimitLabel>
          <StyledLabel>
            {t('{{totalCount}} of {{maxLength}} total characters', {
              totalCount,
              maxLength,
            })}
          </StyledLabel>
        </StyledLabelRowWrapper>
      )}
      <Validation inModal={inModal} {...validationProps} />
    </StyledLabelWrapper>
  );
};

export default TextTemplater;
